import { useState } from "react";
import { Ban } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { blockUser } from "../lib/moderation";
import { toast } from "sonner";

/**
 * BlockUserButton — Guideline 1.2 compliant single-tap block control.
 *
 * Shown anywhere user-generated content is displayed (waste post header,
 * bin listing owner row, bid rows in WasteDetail). Opens a small confirm
 * dialog then calls `POST /users/blocks`.
 *
 * @param {object}   props
 * @param {string}   props.currentUserId    Logged-in user id.
 * @param {string}   props.targetUserId     User being blocked.
 * @param {string}   props.targetUserName   Display name (for confirm copy).
 * @param {string=}  props.label            Button label override (default "Block").
 * @param {string=}  props.testId
 * @param {string=}  props.className        Extra classes on the button.
 * @param {Function=} props.onBlocked        Fired after successful block.
 */
export default function BlockUserButton({
  currentUserId,
  targetUserId,
  targetUserName,
  label = "Block",
  testId,
  className = "inline-flex items-center gap-1 text-xs text-[#6B6965] hover:text-[#D67B54] underline underline-offset-2",
  onBlocked,
}) {
  const [open, setOpen] = useState(false);
  const [working, setWorking] = useState(false);

  if (!currentUserId || !targetUserId || currentUserId === targetUserId) return null;

  const confirm = async () => {
    setWorking(true);
    try {
      await blockUser({ userId: currentUserId, blockedUserId: targetUserId });
      toast.success(`${targetUserName || "User"} blocked — they can no longer contact you.`);
      setOpen(false);
      if (onBlocked) onBlocked();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to block user");
    } finally {
      setWorking(false);
    }
  };

  return (
    <>
      <button
        type="button"
        data-testid={testId}
        onClick={() => setOpen(true)}
        className={className}
      >
        <Ban className="w-3 h-3" /> {label}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid={testId ? `${testId}-modal` : undefined} className="max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle>Block {targetUserName || "this user"}?</DialogTitle>
            <DialogDescription>
              They&apos;ll no longer be able to message you, bid on your posts, or see your listings. You can unblock any time from Settings.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2 flex-col sm:flex-row">
            <Button
              data-testid={testId ? `${testId}-cancel` : undefined}
              variant="outline"
              onClick={() => setOpen(false)}
              className="rounded-full"
            >
              Cancel
            </Button>
            <Button
              data-testid={testId ? `${testId}-confirm` : undefined}
              onClick={confirm}
              disabled={working}
              className="rounded-full bg-[#D67B54] hover:bg-[#c26b45] text-white"
            >
              {working ? "Blocking…" : "Block user"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
